import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DocumentIngestion_ExtractFieldsResult,
  Estate_FieldsFragment,
  Estate_GetEstateByIdDocument,
  Estate_GetEstatesByPortalIdDocument,
  EstateImport,
  EstateImport_GetAllEstateImportsByPortalIdDocument,
  EstateImportStatus,
  EstateStatus,
  EstateType,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  useEstate_CreateEstateMutation,
  useEstate_UpdateEstateMutation,
} from 'queries';
import { useAnalytics } from 'use-analytics';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { AnalyticsEvent } from 'utils/enum';
import { getEstateUpdateEstateInputData, isEstateEditable } from 'utils/estate';
import { GridStateKey, refreshGridState } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useIsImpersonating from 'utils/useIsImpersonating';

import { EstateDetailDocumentIngestionPrompt } from './estate-detail-document-ingestion-prompt';
import EstateDetailTruadminForm from './estate-detail-truadmin-form';
import EstateDetailTruplanningForm from './estate-detail-truplanning-form';
import EstateDetailTruproxyForm from './estate-detail-truproxy-form';
import EstateDetailTrustadminForm from './estate-detail-trustadmin-form';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    box: {
      position: 'relative',
      minHeight: '100vh',
    },
    headerText: {
      paddingTop: '5px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    form: {
      textAlign: 'left',
      width: '100%',
      position: 'relative',
      '& input': {
        paddingRight: '10px',
        padding: '1vh 2vh',
      },
    },
    previousButton: {
      width: '40%',
      borderRadius: '25px',
      marginRight: theme.spacing(5),
      backgroundColor: 'white',
      color: 'black',
      border: '1px solid black',
    },
    nextButton: {
      width: '40%',
      borderRadius: '25px',
      backgroundColor: '#5ED1F0',
      color: 'black',
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transform: 'translate(-6.1%, 35%)',
      position: 'sticky',
      backgroundColor: 'white',
      bottom: 5,
      padding: theme.spacing(2),
      width: `calc(100% + ${theme.spacing(13)})`,
      zIndex: 10,
      borderTop: '1px solid rgb(188, 188, 188)',
    },
  }),
);

const schema = yup.object().shape({
  type: yup.string().required(),
  estateTruadminFields: yup.object().when('type', {
    is: EstateType.Truadmin,
    then: (schema) =>
      schema.shape({
        clientFirstName: yup.string().required('Required'),
        clientLastName: yup.string().required('Required'),
        decedentFirstName: yup.string().required('Required'),
        decedentLastName: yup.string().required('Required'),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTruproxyFields: yup.object().when('type', {
    is: EstateType.Truproxy,
    then: (schema) =>
      schema.shape({
        wardFirstName: yup.string().required('Required'),
        wardLastName: yup.string().required('Required'),
        clientFirstName: yup.string().required('Required'),
        clientLastName: yup.string().required('Required'),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTruplanningFields: yup.object().when('type', {
    is: EstateType.Truplanning,
    then: (schema) =>
      schema.shape({
        clientFirstName: yup.string().required('Required'),
        clientLastName: yup.string().required('Required'),
        spouseFirstName: yup.string().when('isClientMarried', {
          is: true,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.notRequired(),
        }),
        spouseLastName: yup.string().when('isClientMarried', {
          is: true,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTrustadminFields: yup.object().when('type', {
    is: EstateType.Trustadmin,
    then: (schema) =>
      schema.shape({
        trustName: yup.string().required('Required'),
        estateGrantors: yup
          .array()
          .min(1, 'At least one grantor is required')
          .of(
            yup.object().shape({
              grantorFirstName: yup.string().required('Required'),
              grantorLastName: yup.string().required('Required'),
            }),
          ),
        estateTrustees: yup
          .array()
          .min(1, 'At least one trustee is required')
          .of(
            yup.object().shape({
              trusteeFirstName: yup.string().required('Required'),
              trusteeLastName: yup.string().required('Required'),
              isPrimaryTrustee: yup.boolean().required('Required'),
              trusteePhoneNumber: yup.string().when('isPrimaryTrustee', {
                is: true,
                then: (schema) => schema.required('Please enter a phone number'),
                otherwise: (schema) => schema.nullable(),
              }),
            }),
          )
          .test(
            'estateTrustees',
            () => 'Select a primary point of contact',
            (trustees) => {
              const primaryTrustees = trustees?.find((trustee) => trustee.isPrimaryTrustee);
              return Boolean(primaryTrustees);
            },
          )
          .test(
            'estateTrustees',
            () => 'Select only one primary point of contact',
            (trustees) => {
              const primaryTrustees = trustees?.filter((trustee) => trustee.isPrimaryTrustee) || [];
              return primaryTrustees.length === 1;
            },
          ),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
});

type Props = {
  handleBack: () => void;
  onSubmit: (estate: Estate_FieldsFragment) => void;
  editingEstate?: Estate_FieldsFragment;
  estateImport?: EstateImport | null;
  estateType: EstateType;
  isEditing: boolean;
};

const EstateDetailForm = ({
  handleBack,
  onSubmit,
  editingEstate,
  estateImport,
  estateType,
  isEditing,
}: Props) => {
  const classes = useStyles();
  const analytics = useAnalytics();
  const { portalId } = useParams<Record<string, string>>();
  const isImpersonating = useIsImpersonating();
  const backButtonLabel = editingEstate?.id ? 'Exit' : 'Previous';

  // Decedent details are only editable if:
  // 1) TSA is impersonating
  // 2) it is a new estate
  // 3) existing estate before payment is made
  const isEditable = Boolean(
    !isEditing ||
      isImpersonating ||
      (editingEstate?.status && isEstateEditable(editingEstate.status)),
  );

  const defaultValues: any = editingEstate ? getEstateUpdateEstateInputData(editingEstate) : {};

  // Populate form with values from the imported estate data
  if (!editingEstate && estateImport) {
    if (estateType === EstateType.Truadmin) {
      defaultValues.estateTruadminFields = {
        decedentFirstName: estateImport.decedentFirstName,
        decedentLastName: estateImport.decedentLastName,
        decedentDateOfBirth: estateImport.decedentDateOfBirth,
        decedentDateOfDeath: estateImport.decedentDateOfDeath,
        decedentSsn: estateImport.decedentSsn,
        decedentLastAddressStreet: estateImport.decedentLastAddressStreet,
        decedentLastAddressCity: estateImport.decedentLastAddressCity,
        decedentLastAddressState: estateImport.decedentLastAddressState,
        decedentLastAddressZip: estateImport.decedentLastAddressZip,
        clientFirstName: estateImport.clientFirstName,
        clientLastName: estateImport.clientLastName,
        clientEmailAddress: estateImport.clientEmailAddress,
        clientPhoneNumber: estateImport.clientPhoneNumber,
        clientAddressStreet: estateImport.clientAddressStreet,
        clientAddressCity: estateImport.clientAddressCity,
        clientAddressState: estateImport.clientAddressState,
        clientAddressZip: estateImport.clientAddressZip,
      };
    } else if (estateType === EstateType.Truplanning) {
      defaultValues.estateTruplanningFields = {
        clientFirstName: estateImport.clientFirstName,
        clientLastName: estateImport.clientLastName,
        clientEmailAddress: estateImport.clientEmailAddress,
        clientPhoneNumber: estateImport.clientPhoneNumber,
        clientSsn: estateImport.clientSsn,
        clientDateOfBirth: estateImport.clientDateOfBirth,
        clientAddressStreet: estateImport.clientAddressStreet,
        clientAddressCity: estateImport.clientAddressCity,
        clientAddressState: estateImport.clientAddressState,
        clientAddressZip: estateImport.clientAddressZip,
        isClientMarried: estateImport.isClientMarried || false,
        spouseFirstName: estateImport.spouseFirstName,
        spouseLastName: estateImport.spouseLastName,
        spouseMiddleName: estateImport.spouseMiddleName,
        spouseEmailAddress: estateImport.spouseEmailAddress,
        spousePhoneNumber: estateImport.spousePhoneNumber,
        spouseSsn: estateImport.spouseSsn,
        spouseDOB: estateImport.spouseDOB,
        spouseAddressStreet: estateImport.spouseAddressStreet,
        spouseAddressCity: estateImport.spouseAddressCity,
        spouseAddressState: estateImport.spouseAddressState,
        spouseAddressZip: estateImport.spouseAddressZip,
      };
    } else if (estateType === EstateType.Truproxy) {
      defaultValues.estateTruproxyFields = {
        wardFirstName: estateImport.wardFirstName,
        wardLastName: estateImport.wardLastName,
        wardMiddleName: estateImport.wardMiddleName,
        wardSsn: estateImport.wardSsn,
        wardDateOfBirth: estateImport.wardDateOfBirth,
        wardAddressStreet: estateImport.wardAddressStreet,
        wardAddressCity: estateImport.wardAddressCity,
        wardAddressState: estateImport.wardAddressState,
        wardAddressZip: estateImport.wardAddressZip,
        clientFirstName: estateImport.clientFirstName,
        clientLastName: estateImport.clientLastName,
        clientEmailAddress: estateImport.clientEmailAddress,
        clientPhoneNumber: estateImport.clientPhoneNumber,
      };
    } else if (estateType === EstateType.Trustadmin) {
      defaultValues.estateTrustadminFields = {
        trustName: estateImport.trustName,
        trustDate: estateImport.trustDate,
        isGrantorTrust: estateImport.isGrantorTrust,
        isRevocableTrust: estateImport.isRevocableTrust,
        ein: estateImport.ein,
      };

      if (estateImport.trusteeFirstName && estateImport.trusteeLastName) {
        defaultValues.estateTrustadminFields.estateTrustees = [
          {
            isPrimaryTrustee: true,
            trusteeFirstName: estateImport.trusteeFirstName,
            trusteeLastName: estateImport.trusteeLastName,
            trusteeMiddleName: estateImport.trusteeMiddleName,
            trusteeEmail: estateImport.trusteeEmail,
            trusteePhoneNumber: estateImport.trusteePhoneNumber,
            trusteeAddressStreet: estateImport.trusteeAddressStreet,
            trusteeAddressCity: estateImport.trusteeAddressCity,
            trusteeAddressState: estateImport.trusteeAddressState,
            trusteeAddressZip: estateImport.trusteeAddressZip,
          },
        ];
      }
    }
  }

  if (
    estateType === EstateType.Trustadmin &&
    !defaultValues.estateTrustadminFields?.estateGrantors
  ) {
    defaultValues.estateTrustadminFields = {
      ...defaultValues.estateTrustadminFields,
      estateGrantors: [{ isPrimaryGrantor: true, isGrantorStillAlive: false }], // ensure at least one primary grantor
    };
  }

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      type: estateType,
    },
  });

  if (Object.keys(formMethods.formState.errors).length > 0) {
    console.warn(formMethods.formState.errors);
  }

  formMethods.watch('estateTrustadminFields.estateTrustees'); // force rerender when trustees change. This is needed for the copy grantor to trustee button

  const [createEstate, { loading: createEstateExecuting }] = useEstate_CreateEstateMutation({
    refetchQueries: [
      {
        query: Estate_GetEstatesByPortalIdDocument,
        variables: { input: { portalId } },
      },
      {
        query: EstateImport_GetAllEstateImportsByPortalIdDocument,
        variables: {
          input: {
            portalId: portalId,
            filter: {
              status: EstateImportStatus.Pending,
            },
          },
        },
      },
    ],
    onCompleted: (data) => {
      analytics.track(AnalyticsEvent.ESTATE_CREATED, {
        'Estate ID': data.data.id,
        'Estate Name': data.data.estateName,
        'Estate Type': data.data.type,
      });
      onSubmit(data.data);
      refreshGridState(GridStateKey.EstateList);
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateEstate] = useEstate_UpdateEstateMutation({
    refetchQueries: [
      {
        query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
        variables: { portalId },
      },
      {
        query: Estate_GetEstatesByPortalIdDocument,
        variables: { input: { portalId } },
      },
      {
        query: Estate_GetEstateByIdDocument,
        variables: { id: editingEstate?.id },
      },
    ],
    onCompleted: (data) => onSubmit(data.data),
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submitForm = async (formData: any) => {
    const commonInput = {
      ...formData,
      portalId,
    };

    if (formData.estateTruplanningFields?.clientPhoneNumber === '+1') {
      formData.estateTruplanningFields.clientPhoneNumber = undefined;
    }

    if (formData.estateTruadminFields?.clientPhoneNumber === '+1') {
      formData.estateTruadminFields.clientPhoneNumber = undefined;
    }

    if (formData.estateTruproxyFields?.clientPhoneNumber === '+1') {
      formData.estateTruproxyFields.clientPhoneNumber = undefined;
    }

    if (editingEstate?.id) {
      delete commonInput.type;
      updateEstate({
        variables: {
          input: {
            ...commonInput,
            id: editingEstate.id,
            // push the status forward if it is not further along than this form
            status:
              formData.status === EstateStatus.DetailEntry
                ? EstateStatus.DocumentUpload
                : formData.status,
          },
        },
      });
    } else {
      // creating a new estate
      createEstate({
        variables: {
          input: {
            ...commonInput,
            status: EstateStatus.DocumentUpload,
            estateImportId: estateImport?.id,
          },
        },
      });
    }
  };

  const onExtractFields = (fields: DocumentIngestion_ExtractFieldsResult) => {
    switch (estateType) {
      case EstateType.Truadmin:
        formMethods.setValue('estateTruadminFields.decedentFirstName', fields.firstName);
        formMethods.setValue('estateTruadminFields.decedentMiddleName', fields.middleName);
        formMethods.setValue('estateTruadminFields.decedentLastName', fields.lastName);
        formMethods.setValue('estateTruadminFields.decedentDateOfBirth', fields.dateOfBirth);
        formMethods.setValue('estateTruadminFields.decedentDateOfDeath', fields.dateOfDeath);
        formMethods.setValue('estateTruadminFields.decedentSsn', fields.ssn);
        formMethods.setValue(
          'estateTruadminFields.decedentLastAddressStreet',
          fields.lastAddressStreet,
        );
        formMethods.setValue(
          'estateTruadminFields.decedentLastAddressCity',
          fields.lastAddressCity,
        );
        formMethods.setValue(
          'estateTruadminFields.decedentLastAddressState',
          fields.lastAddressState,
        );
        formMethods.setValue('estateTruadminFields.decedentLastAddressZip', fields.lastAddressZip);
        break;
      case EstateType.Trustadmin:
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorFirstName',
          fields.firstName,
        );
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorLastName',
          fields.lastName,
        );
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorMiddleName',
          fields.middleName,
        );
        formMethods.setValue('estateTrustadminFields.estateGrantors.0.grantorSsn', fields.ssn);
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorAddressStreet',
          fields.lastAddressStreet,
        );
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorAddressState',
          fields.lastAddressState,
        );
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorAddressCity',
          fields.lastAddressCity,
        );
        formMethods.setValue(
          'estateTrustadminFields.estateGrantors.0.grantorAddressZip',
          fields.lastAddressZip,
        );
        break;
      case EstateType.Truplanning:
        formMethods.setValue('estateTruplanningFields.clientFirstName', fields.firstName);
        formMethods.setValue('estateTruplanningFields.clientLastName', fields.lastName);
        formMethods.setValue('estateTruplanningFields.clientMiddleName', fields.middleName);
        formMethods.setValue('estateTruplanningFields.clientSsn', fields.ssn);
        formMethods.setValue('estateTruplanningFields.clientDateOfBirth', fields.dateOfBirth);
        formMethods.setValue(
          'estateTruplanningFields.clientAddressStreet',
          fields.lastAddressStreet,
        );
        formMethods.setValue('estateTruplanningFields.clientAddressCity', fields.lastAddressCity);
        formMethods.setValue('estateTruplanningFields.clientAddressState', fields.lastAddressState);
        formMethods.setValue('estateTruplanningFields.clientAddressZip', fields.lastAddressZip);
        break;
      case EstateType.Truproxy:
        formMethods.setValue('estateTruproxyFields.wardFirstName', fields.firstName);
        formMethods.setValue('estateTruproxyFields.wardLastName', fields.lastName);
        formMethods.setValue('estateTruproxyFields.wardMiddleName', fields.middleName);
        formMethods.setValue('estateTruproxyFields.wardSsn', fields.ssn);
        formMethods.setValue('estateTruproxyFields.wardDateOfBirth', fields.dateOfBirth);
        formMethods.setValue('estateTruproxyFields.wardAddressStreet', fields.lastAddressStreet);
        formMethods.setValue('estateTruproxyFields.wardAddressCity', fields.lastAddressCity);
        formMethods.setValue('estateTruproxyFields.wardAddressState', fields.lastAddressState);
        formMethods.setValue('estateTruproxyFields.wardAddressZip', fields.lastAddressZip);
        break;
    }
  };

  return (
    <Box className={classes.box}>
      <EstateDetailDocumentIngestionPrompt
        portalId={portalId}
        onExtractFields={onExtractFields}
        estateType={estateType}
      />

      <Typography variant="h5" className={classes.headerText}>
        Please fill out the details:
      </Typography>
      <FormProvider {...formMethods}>
        <form className={classes.form} onSubmit={formMethods.handleSubmit(submitForm)}>
          <div>
            <Controller
              name="estateNumber"
              control={formMethods.control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  label="Estate Number"
                  errorMessage={
                    formMethods.formState.errors.estateNumber?.message
                      ? String(formMethods.formState.errors.estateNumber?.message)
                      : ''
                  }
                />
              )}
            />
          </div>
          {estateType === EstateType.Truadmin && (
            <EstateDetailTruadminForm portalId={portalId} isEditable={isEditable} />
          )}
          {estateType === EstateType.Truplanning && (
            <EstateDetailTruplanningForm portalId={portalId} isEditable={isEditable} />
          )}
          {estateType === EstateType.Truproxy && (
            <EstateDetailTruproxyForm portalId={portalId} isEditable={isEditable} />
          )}
          {estateType === EstateType.Trustadmin && <EstateDetailTrustadminForm />}
          <div className={classes.footer}>
            <Button
              className={classes.previousButton}
              color="primary"
              onClick={handleBack}
              variant="contained"
            >
              {backButtonLabel}
            </Button>
            <Button
              className={classes.nextButton}
              type="submit"
              color="secondary"
              disabled={formMethods.formState.isSubmitting || createEstateExecuting}
              variant="contained"
            >
              Next
            </Button>
          </div>
        </form>
      </FormProvider>
    </Box>
  );
};

export default EstateDetailForm;
